import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { KarelCodeEditor } from "./karelCodeEditor";
import { isDarkTheme, useThemeStore } from "../app/stores/themeStore";
import styles from "./CodeMirrorEditor.module.css";

export interface CodeMirrorEditorHandle {
  getEditor(): KarelCodeEditor | null;
}

interface CodeMirrorEditorProps {
  readOnly?: boolean;
  value?: string;
}

export const CodeMirrorEditor = forwardRef<
  CodeMirrorEditorHandle,
  CodeMirrorEditorProps
>(function CodeMirrorEditor({ readOnly = false, value = "" }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<KarelCodeEditor | null>(null);
  const languageMode = useThemeStore((s) => s.languageMode);
  const lintingEnabled = useThemeStore((s) => s.lintingEnabled);
  const themeId = useThemeStore((s) => s.themeId);

  useImperativeHandle(ref, () => ({ getEditor: () => editorRef.current }));

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const editor = new KarelCodeEditor(container, {
      readOnly,
      value,
      language: useThemeStore.getState().languageMode,
      linting: useThemeStore.getState().lintingEnabled,
    });
    editor.applyTheme(
      isDarkTheme(useThemeStore.getState().themeId) ? "dark" : "light",
    );
    editorRef.current = editor;
    return () => {
      editor.destroy();
      editorRef.current = null;
    };
  }, []);

  useEffect(() => {
    editorRef.current?.setLanguage(languageMode);
  }, [languageMode]);

  useEffect(() => {
    editorRef.current?.setLinting(lintingEnabled);
  }, [lintingEnabled]);

  useEffect(() => {
    editorRef.current?.applyTheme(isDarkTheme(themeId) ? "dark" : "light");
  }, [themeId]);

  return <div ref={containerRef} className={styles.editor} />;
});
